import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TextInput from '../../forms/TextInput';
import * as userSlice from '../../../data/user.slice';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useOnSaved } from '../../../hooks/useOnSaved';
import { UserRole } from '../../../data/models/user-role.model';
import { usePortalClientBC } from '../../../hooks/usePortalClientBC';
import { usePortalName } from '../../../hooks/usePortalName';
import { UserAccount } from '../../../data/models/user-account.model';

const PREFIX = 'AddUserDialog';

const classes = {
  content: `${PREFIX}-content`,
  nameFields: `${PREFIX}-nameFields`,
  actions: `${PREFIX}-actions`,
  progress: `${PREFIX}-progress`,
  roleHelperText: `${PREFIX}-roleHelperText`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-container': {
    alignItems: 'flex-start',
  },
  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
    width: 380,
  },

  [`& .${classes.nameFields}`]: {
    display: 'flex',
    gap: theme.spacing(2),
    '& > *': {
      flex: 1,
    },
  },

  [`& .${classes.actions}`]: {
    gap: theme.spacing(1),
  },

  [`& .${classes.progress}`]: {
    width: '100%',
    color: theme.palette.primary.main,
  },

  [`& .${classes.roleHelperText}`]: {
    marginTop: theme.spacing(0.5),
    marginLeft: theme.spacing(1.5),
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
}));

interface AddUserDialogProps {
  user?: UserAccount;
  userRoles: UserRole[];
  open: boolean;
  close: () => void;
}

const getUserDefaults = (user?: UserAccount): any => {
  return {
    FirstName: user?.userFirstName || '',
    LastName: user?.userLastName || '',
    Email: user?.emailAddress || '',
    Role: user?.userAccountRoles?.[0]?.userRoleID || '',
  };
};

export default function AddUserDialog({
  user,
  userRoles,
  open,
  close,
}: AddUserDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const requiredFieldMessage = t('common.requiredField');
  const portalName = usePortalName();
  const clientBC = usePortalClientBC(portalName || '');
  const isEditMode = Boolean(user);
  const savingUser = useAppSelector(userSlice.selectSavingUser);
  const savedUser = useAppSelector(userSlice.selectSavedUser);

  const formHook = useForm({
    mode: 'onTouched',
    defaultValues: getUserDefaults(user),
  });

  React.useEffect(() => {
    formHook.reset(getUserDefaults(user));
  }, [open]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!open) return;
      if (event.key === 'Enter') {
        event.preventDefault();
        if (formHook.formState.isValid && !savingUser) {
          formHook.handleSubmit(handleSave)();
        }
      }
    };
    if (open) document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, formHook.formState.isValid, savingUser, formHook]);

  const handleClose = () => {
    formHook.reset();
    close();
  };

  useOnSaved(savedUser, handleClose);

  const handleSave = (data: any) => {
    if (isEditMode) {
      if (user?.userAccountID) {
        let payload = {
          userFirstName: data.FirstName,
          userLastName: data.LastName,
        };
        dispatch(userSlice.updateClientUserAsync(user.userAccountID, payload));
      } else {
        return;
      }
    } else {
      if (!clientBC) return;
      const payload = {
        emailAddress: data.Email,
        userFirstName: data.FirstName,
        userLastName: data.LastName,
        clientBC: clientBC,
        roleId: Number(data.Role),
      };

      dispatch(userSlice.createClientUserAsync(payload.clientBC, payload));
    }
  };

  return (
    <RootDialog open={open} onClose={handleClose}>
      <DialogTitle>
        {isEditMode ? t('client.editUser') : t('client.addUser')}
        {savingUser && <LinearProgress className={classes.progress} />}
      </DialogTitle>
      <DialogContent>
        <div className={classes.content}>
          <Box className={classes.nameFields}>
            <TextInput
              name="FirstName"
              label={t('passenger.firstName')}
              control={formHook.control}
              required
              rules={{ required: requiredFieldMessage }}
            />
            <TextInput
              name="LastName"
              label={t('passenger.lastName')}
              control={formHook.control}
              required
              rules={{ required: requiredFieldMessage }}
            />
          </Box>
          <TextInput
            name="Email"
            label={t('client.email')}
            control={formHook.control}
            errors={formHook.formState.errors}
            required
            rules={{
              required: requiredFieldMessage,
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: t('member.invalidEmailAddress'),
              },
            }}
            readOnly={isEditMode ? true : false}
          />
          <Controller
            name="Role"
            control={formHook.control}
            rules={{ required: requiredFieldMessage }}
            render={({ field }) => {
              const selectedRole = userRoles.find(
                (role) => role.userRoleID === Number(field.value),
              );
              const helperTextKey = selectedRole?.userRoleBC
                ? `client.roleHelper.${selectedRole.userRoleBC}`
                : '';
              const helperText = t(helperTextKey, { defaultValue: '' });

              return (
                <Box>
                  <TextField
                    {...field}
                    select
                    label={t('client.role')}
                    required
                    fullWidth
                    InputProps={{
                      readOnly: isEditMode ? true : false,
                    }}>
                    {userRoles.map((role) => (
                      <MenuItem key={role.userRoleID} value={role.userRoleID}>
                        {role.userRoleName}
                      </MenuItem>
                    ))}
                  </TextField>

                  {helperText && (
                    <Typography
                      variant="body2"
                      className={classes.roleHelperText}>
                      {helperText}
                    </Typography>
                  )}
                </Box>
              );
            }}
          />
        </div>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          variant="contained"
          size="large"
          color="secondary"
          onClick={handleClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          disabled={!formHook.formState.isValid || savingUser}
          onClick={formHook.handleSubmit(handleSave)}>
          {isEditMode ? t('common.save') : t('common.add')}
        </Button>
      </DialogActions>
    </RootDialog>
  );
}
